import {
  createApi,
  fetchBaseQuery
} from '@reduxjs/toolkit/query/react';
import {
  getCookie, setCookie, handleLogoutRedirect, Logger
} from '../../utilities';
import {
  BASE_API_URL, endpoints, ACCESS_TOKEN_AGE, REFRESH_TOKEN_AGE
} from '../../constants';

const baseQuery = fetchBaseQuery({
  baseUrl: `${BASE_API_URL}`,
  credentials: 'same-origin',
  prepareHeaders: (headers) => {
    const token = getCookie('_tk');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
});

let refreshPromise = null;

const attemptRefresh = async () => {
  const rtk = getCookie('_rtk');
  if (!rtk) return null;

  try {
    const res = await fetch(`${BASE_API_URL}${endpoints.accounts.refresh}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rtk })
    });
    return res.ok ? res.json() : null;
  } catch {
    return null;
  }
};

const SKIP_REFRESH = [endpoints.accounts.login, endpoints.accounts.refresh];

const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (!result.error || result.error.status !== 401) return result;
  if (SKIP_REFRESH.includes(args?.url)) return result;

  try {
    if (!refreshPromise) {
      refreshPromise = attemptRefresh().finally(() => {
        refreshPromise = null;
      });
    }

    const refreshResult = await refreshPromise;

    if (refreshResult?.loginToken) {
      setCookie('_tk', refreshResult.loginToken, ACCESS_TOKEN_AGE);
      if (refreshResult?.refreshToken) {
        setCookie('_rtk', refreshResult.refreshToken, REFRESH_TOKEN_AGE);
      }
      Logger('Token refreshed — retrying request');
      return baseQuery(args, api, extraOptions);
    }
  } catch (err) {
    Logger('Token refresh failed', err);
  }

  Logger('Could not refresh token — logging out');
  handleLogoutRedirect();
  return result;
};

const apiSlice = createApi({
  reducerPath: 'api-slice',
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true
});

export default apiSlice;
